import './issuer-compliance-rules.scss'

import { Button, Card, Select, Table, Typography } from 'antd'
import classnames from 'classnames'
import React, { useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../../api'
import { momentDateHandler } from '../../../../date/moment-date-handler'
import { CreateIssuerComplianceRuleDrawer } from './CreateIssuerComplianceRule'
import { DeleteIssuerComplianceRuleModal } from './DeleteIssuerComplianceRule'
import { buildIssuerComplianceRulesTableColumns } from './issuer-compliance-rules-table-columns'
import { IssuerComplianceRule } from './models'
import { UpdateIssuerComplianceRuleDrawer } from './UpdateIssuerComplianceRule'

const { formatDate, newDate } = momentDateHandler

interface IssuerComplianceRuleResponse {
  list: IssuerComplianceRule[]
}

export const IssuerComplianceRules = ({ tenant }) => {
  const [currentGroupId, setCurrentGroupId] = useState(null)
  const [ruleToUpdate, setRuleToUpdate] = useState(null)
  const [ruleToDelete, setRuleToDelete] = useState(null)
  const [isCreatingRule, setIsCreatingRule] = useState(false)

  const { data: issuerCompliancesGroups } = useSWR(
    tenant ? ['/compliances/issuers/groups', tenant] : null,
    fetchDetailByDomain,
  )

  const { data: issuerCompliances, mutate } = useSWR<IssuerComplianceRuleResponse>(
    currentGroupId ? [`/compliances/issuers/groups/${currentGroupId}/rules`, tenant] : null,
    fetchDetailByDomain,
  )

  const groupOptions = issuerCompliancesGroups
    ? issuerCompliancesGroups.list.map(({ id, startDate }) => ({
        label: `Group ${id} starting at ${formatDate({ date: newDate(startDate) })}`,
        value: id,
      }))
    : []

  const onUpdate = (issuerComplianceRule: IssuerComplianceRule) => setRuleToUpdate(issuerComplianceRule)

  const onDelete = (issuerComplianceRule: IssuerComplianceRule) => setRuleToDelete(issuerComplianceRule)

  const onCloseDialogs = () => {
    setIsCreatingRule(false)
    setRuleToUpdate(null)
    setRuleToDelete(null)
  }

  const onSuccess = () => {
    onCloseDialogs()
    mutate()
  }

  const columns = buildIssuerComplianceRulesTableColumns({ onUpdate, onDelete })

  return (
    <Column className="issuer-compliance-rules">
      <Card loading={!issuerCompliancesGroups}>
        <Select
          options={groupOptions}
          placeholder="Select a counterparty compliance group"
          onSelect={setCurrentGroupId}
        />
      </Card>

      <Card
        title={<ComplianceTitle currentGroupId={currentGroupId} onCreate={setIsCreatingRule} />}
        loading={!issuerCompliancesGroups}
      >
        {!issuerCompliances ? (
          <p>Please select a counterparty compliance group above</p>
        ) : (
          <Table
            rowClassName={(issuerComplianceRule: IssuerComplianceRule) =>
              classnames({ active: issuerComplianceRule.id === (ruleToUpdate?.id || ruleToDelete?.id) })
            }
            dataSource={issuerCompliances?.list}
            loading={!issuerCompliances}
            columns={columns}
            pagination={false}
            rowKey="id"
            size="small"
          />
        )}
      </Card>

      <CreateIssuerComplianceRuleDrawer
        domain={tenant}
        groupId={currentGroupId}
        isVisible={isCreatingRule}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <UpdateIssuerComplianceRuleDrawer
        domain={tenant}
        groupId={currentGroupId}
        issuerComplianceRule={ruleToUpdate}
        isVisible={currentGroupId && ruleToUpdate?.id}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <DeleteIssuerComplianceRuleModal
        domain={tenant}
        complianceGroupId={currentGroupId}
        issuerComplianceRule={ruleToDelete}
        isVisible={currentGroupId && ruleToDelete?.id}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />
    </Column>
  )
}

const ComplianceTitle = ({ currentGroupId, onCreate }) => (
  <Row alignContentCenter justifyContentSpaceBetween>
    <Typography.Title level={3}>{`Counterparty Compliance ${
      currentGroupId ? 'for Group ' + currentGroupId : ''
    } `}</Typography.Title>
    {currentGroupId && (
      <Button type="primary" onClick={onCreate}>
        Create Rule
      </Button>
    )}
  </Row>
)
